import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Icon,
    IconButton,
    Modal,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ZendeskIcon from "../../../static/zendesk-icon.svg";
import { Add, Close, Edit, Info } from "@mui/icons-material";
import { useZendeskTicketingSignIn } from "../../../hooks/useZendeskTicketingSignIn";
import { useZendeskMessagingSignIn } from "../../../hooks/useZendeskMessagingSignIn";

const ZendeskIntegration = () => {
    const [open, setOpen] = useState(false);
    const [integrationStatus, setIntegrationStatus] = useState("waiting");
    const [integrationData, setIntegrationData] = useState({});

    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const refreshIntegrationStatus = useCallback(async () => {
        setIntegrationStatus("waiting");

        const response = await fetch(
            `${backendEndpoint}/integration/zendesk/`,
            {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            },
        );

        const json = await response.json();

        if (!json.ticketing) {
            setIntegrationData({});
            setIntegrationStatus("add");
            return;
        }

        setIntegrationData(json);
        setIntegrationStatus("edit");
    }, [backendEndpoint, getCookieValue]);

    const handleEditIntegration = async () => {
        await fetch(`${backendEndpoint}/integration/zendesk/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
            body: JSON.stringify({
                online: integrationData.online,
            }),
        });

        setOpen(false);
        refreshIntegrationStatus();
    };

    const handleDeleteIntegration = async () => {
        await fetch(`${backendEndpoint}/integration/zendesk/`, {
            method: "DELETE",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        setOpen(false);
        refreshIntegrationStatus();
    };

    useEffect(() => {
        refreshIntegrationStatus();
    }, [refreshIntegrationStatus]);

    return (
        <>
            <Box
                id="zendesk-integration-box"
                sx={{
                    height: 200,
                    width: 250,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Icon
                        sx={{
                            fontSize: 24,
                            textAlign: "center",
                            position: "relative",
                        }}
                    >
                        <img
                            src={ZendeskIcon}
                            alt="Zendesk"
                            style={{
                                width: "100%",
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                            }}
                        />
                    </Icon>
                    <Typography sx={{ fontSize: 24, ml: 1 }} variant="p">
                        Zendesk
                    </Typography>
                </Box>
                <Button
                    sx={{ mt: 4 }}
                    variant="contained"
                    startIcon={
                        integrationStatus === "add" ? (
                            <Add />
                        ) : integrationStatus === "waiting" ? (
                            <CircularProgress size="15px" color="inherit" />
                        ) : (
                            <Edit />
                        )
                    }
                    onClick={
                        integrationStatus === "waiting"
                            ? null
                            : () => setOpen(true)
                    }
                >
                    {integrationStatus === "add"
                        ? "Adicionar"
                        : integrationStatus === "waiting"
                          ? "Verificando..."
                          : "Editar"}
                </Button>
            </Box>

            <Modal
                open={open}
                //  onClose={() => setOpen(false)}
            >
                <Box
                    sx={{
                        width: "400px",
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "10%",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={() => setOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" gutterBottom>
                        Integração com Zendesk
                    </Typography>

                    {integrationData.ticketing !== true ? (
                        <ZendeskTicketingConnectButton
                            onAuthenticated={() => refreshIntegrationStatus()}
                        />
                    ) : (
                        <>
                            {integrationData.messaging !== true ? (
                                <ZendeskMessagingConnectButton
                                    onAuthenticated={() =>
                                        refreshIntegrationStatus()
                                    }
                                />
                            ) : (
                                <Box
                                    sx={{
                                        backgroundColor: "#bbbbff",
                                        color: "#444444",
                                        p: 2,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="p" gutterBottom>
                                        Acesse{" "}
                                        <a
                                            href={`https://${integrationData.subdomain}.zendesk.com/admin/ai/ai-agents/ai-agents/marketplace-bots`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            aqui
                                        </a>{" "}
                                        e defina o Chiko como padrão
                                    </Typography>
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                label="Nome"
                                variant="outlined"
                                value={integrationData.name}
                                sx={{ mt: 2 }}
                                disabled
                                slotProps={{
                                    htmlInput: {
                                        readOnly: true,
                                        disableUnderline: true,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                variant="outlined"
                                value={integrationData.email}
                                sx={{ mt: 2 }}
                                disabled
                                slotProps={{
                                    htmlInput: {
                                        readOnly: true,
                                        disableUnderline: true,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Organização"
                                variant="outlined"
                                value={integrationData.organizationName}
                                sx={{ mt: 2 }}
                                disabled
                                slotProps={{
                                    htmlInput: {
                                        readOnly: true,
                                        disableUnderline: true,
                                    },
                                }}
                            />
                            <FormGroup>
                                <FormControlLabel
                                    label="Online"
                                    sx={{ mt: 2 }}
                                    control={
                                        <Switch
                                            checked={integrationData.online}
                                            onChange={(e) =>
                                                setIntegrationData({
                                                    ...integrationData,
                                                    online: e.target.checked,
                                                })
                                            }
                                        />
                                    }
                                />
                            </FormGroup>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={() => handleEditIntegration()}
                                sx={{ mt: 2 }}
                            >
                                Salvar
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteIntegration()}
                                sx={{ mt: 2 }}
                            >
                                Deletar
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    );
};

const ZendeskTicketingConnectButton = ({ onAuthenticated }) => {
    const [loading, setLoading] = useState(false);
    const [subdomain, setSubdomain] = useState("");
    const subdomainRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])$/;

    const { fetchPopupUrl, openPopup, monitorPopup, completeAuthentication } =
        useZendeskTicketingSignIn();

    const handleOnClick = async () => {
        const isSubdomainValid = subdomainRegex.test(subdomain);
        if (!isSubdomainValid) return;

        setLoading(true);

        const popupUrl = await fetchPopupUrl(subdomain);
        openPopup(popupUrl, "Zendesk Ticketing Sign-In");

        const popupMonitor = monitorPopup();
        popupMonitor
            .then(async (code) => {
                await completeAuthentication(subdomain, code);
                onAuthenticated();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <Box sx={{ mt: 2, display: "flex" }}>
                <TextField
                    label="Subdomínio"
                    variant="outlined"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "&:not(:hover):not(.Mui-focused) fieldset": {
                                borderRadius: "4px 0px 0px 4px",
                                borderRightWidth: 0,
                            },
                        },
                    }}
                />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        backgroundColor: "#efefef",
                        color: "#000000",
                        borderRadius: "0px 4px 4px 0px",
                        border: "0.8px solid rgba(0, 0, 0, 0.23)",
                        borderLeftWidth: 0,
                    }}
                >
                    .zendesk.com
                </Box>
            </Box>
            <Button
                onClick={() => (loading ? null : handleOnClick())}
                disabled={loading}
                fullWidth
                sx={{
                    marginTop: 2,
                    backgroundColor: "#dedede",
                    color: "#000000",
                    "&:hover": {
                        backgroundColor: "#cdcdcd",
                    },
                }}
            >
                {loading ? (
                    <>
                        <CircularProgress
                            color="rgba(0, 0, 0, 0.26)"
                            size={20}
                            sx={{ marginRight: 1 }}
                        />
                        Carregando
                    </>
                ) : (
                    <>
                        <Icon
                            sx={{
                                fontSize: 24,
                                textAlign: "center",
                                position: "relative",
                                marginRight: 1,
                            }}
                        >
                            <img
                                src={ZendeskIcon}
                                alt="Zendesk"
                                style={{
                                    width: "100%",
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        </Icon>
                        Conectar
                    </>
                )}
            </Button>
        </>
    );
};

const ZendeskMessagingConnectButton = ({ onAuthenticated }) => {
    const [loading, setLoading] = useState(false);
    const { fetchPopupUrl, openPopup, monitorPopup, completeAuthentication } =
        useZendeskMessagingSignIn();

    const handleOnClick = async () => {
        setLoading(true);

        const popupUrl = await fetchPopupUrl();
        openPopup(popupUrl, "Zendesk Messaging Sign-In");

        const popupMonitor = monitorPopup();
        popupMonitor
            .then(async (code) => {
                await completeAuthentication(code);
                onAuthenticated();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                }}
            >
                <Tooltip
                    sx={{ p: 0, mr: 0.5 }}
                    title="A conexão inicial com Zendesk permite apenas responder os tickets de e-mail. Recomendamos habilitar o chat para conectar o Chiko em canais de conversa em tempo real, como WhatsApp e Widget."
                >
                    <IconButton>
                        <Info color="info" />
                    </IconButton>
                </Tooltip>
                Habilitar para conversas em tempo real
            </Box>
            <Button
                fullWidth
                onClick={loading ? null : () => handleOnClick()}
                sx={{
                    backgroundColor: "#dedede",
                    color: "#000000",
                    "&:hover": {
                        backgroundColor: "#cdcdcd",
                    },
                }}
            >
                {loading ? (
                    <>
                        <>
                            <CircularProgress
                                color="rgba(0, 0, 0, 0.26)"
                                size={20}
                                sx={{ marginRight: 1 }}
                            />
                            Carregando
                        </>
                    </>
                ) : (
                    <>
                        <Icon
                            sx={{
                                fontSize: 24,
                                textAlign: "center",
                                position: "relative",
                                marginRight: 1,
                            }}
                        >
                            <img
                                src={ZendeskIcon}
                                alt="Zendesk"
                                style={{
                                    width: "100%",
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        </Icon>
                        Habilitar Chat
                    </>
                )}
            </Button>
        </Box>
    );
};

export default ZendeskIntegration;
